// PSD の読み書き (ag-psd、遅延ロード)。ブラウザでは Canvas を使わず imageData で扱う。
import type { Document } from '@/types/document'
import { validateDocument } from '@/model/validate'
import { pixelStore } from '@/pixels/pixelStore'
import { psdToDocument, documentToPsd, type ImportedPsd, type ConversionReport } from './mapping'
import { textRaster, shapeRaster, smartRaster } from '@/pixels/rasterCache'

export function isPsdFile(file: File): boolean {
  return /\.(psd|psb)$/i.test(file.name) || file.type === 'image/vnd.adobe.photoshop'
}

async function loadAgPsd() {
  const [agPsd, init] = await Promise.all([import('ag-psd'), import('./init')])
  init.ensurePsdInitialized()
  return agPsd
}

export async function importPsdBlob(blob: Blob, name: string): Promise<ImportedPsd> {
  const { readPsd } = await loadAgPsd()
  const buf = await blob.arrayBuffer()
  const psd = readPsd(buf, { useImageData: true, skipThumbnail: true, skipLinkedFilesData: true, logMissingFeatures: false })
  const imported = psdToDocument(psd, name)
  const ids = new Set(imported.surfaces.map(s => s.id))
  imported.document = validateDocument(JSON.parse(JSON.stringify(imported.document)), ids)
  return imported
}

export async function exportPsdBlob(doc: Document, composite?: { width: number; height: number; rgba: Uint8ClampedArray }): Promise<{ blob: Blob; report: ConversionReport }> {
  const { writePsd } = await loadAgPsd()
  const { psd, report } = documentToPsd(
    doc,
    {
      read(pixelId) {
        const s = pixelStore.get(pixelId)
        const b = s?.contentBounds()
        if (!s || !b) return null
        return { rect: b, data: s.readRect(b), channels: s.channels }
      },
      readText(layerId) {
        const l = doc.layers[layerId]
        if (!l || (l.kind !== 'text' && l.kind !== 'shape' && l.kind !== 'smart')) return null
        const r = l.kind === 'text' ? textRaster(l) : l.kind === 'shape' ? shapeRaster(l) : smartRaster(l, pixelStore.get(l.sourceId))
        if (!r) return null
        return { rect: r.rect, data: r.surface.readRect({ x: 0, y: 0, w: r.rect.w, h: r.rect.h }) }
      }
    },
    composite
  )
  const bytes = writePsd(psd, { generateThumbnail: false, trimImageData: false, noBackground: true })
  return { blob: new Blob([bytes], { type: 'image/vnd.adobe.photoshop' }), report }
}
